import { useCallback, useEffect, useState } from "react";
import { AlertTriangle, Bot, CheckCircle2, Loader2, MessageSquarePlus, Send, XCircle } from "lucide-react";
import { api, apiError, inr, logError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Empty } from "@/components/Layout";
import { Field } from "@/pages/Leads";
import VoiceInput from "@/components/VoiceInput";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const INTENT_STYLE = {
  "Urgent Requirement": "border-l-red-500",
  "Purchase Request": "border-l-red-500",
  "Stock Shortage": "border-l-amber-500",
  "Stock Consumption": "border-l-emerald-700",
  "Stock Damage": "border-l-orange-500",
  "Stock Received": "border-l-emerald-500",
  "Project Requirement": "border-l-violet-500",
  "Production Requirement": "border-l-violet-500",
  "Maintenance Request": "border-l-slate-400",
  "General Message": "border-l-slate-300",
};

const ACTIONS = [
  ["consume_stock", "Record consumption (stock out)"],
  ["receive_stock", "Record receipt (stock in)"],
  ["damage_stock", "Write off as damaged"],
  ["purchase_request", "Create purchase request"],
  ["review", "Acknowledge only"],
];

function confidenceVariant(band) {
  if (band === "high") return "default";
  if (band === "medium") return "secondary";
  return "destructive";
}

export default function AIRequests() {
  const { can } = useAuth();
  const [requests, setRequests] = useState([]);
  const [summary, setSummary] = useState(null);
  const [shortages, setShortages] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [stock, setStock] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);
  const [decision, setDecision] = useState({ action: "", quantity: "", stock_item_id: "", remark: "", priority: "Medium" });
  const [simOpen, setSimOpen] = useState(false);
  const [sim, setSim] = useState({ employee_id: "", text: "" });
  const [processing, setProcessing] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [a, b, c, d] = await Promise.all([
        api.get("/ai/requests"),
        api.get("/ai/daily-summary"),
        api.get("/ai/recurring-shortages"),
        api.get("/stock"),
      ]);
      setRequests(a.data);
      setSummary(b.data);
      setShortages(c.data);
      setStock(d.data.items);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    api
      .get("/employees")
      .then(({ data }) => setEmployees(data))
      .catch((e) => logError("employee list", e));
  }, [load]);

  const openDecision = (r) => {
    setActive(r);
    setDecision({
      action: r.recommendation?.action || "review",
      quantity: r.recommendation?.quantity ?? r.entities?.quantity ?? "",
      stock_item_id: r.matched_stock_item_id || "",
      remark: "",
      priority: r.entities?.urgency || "Medium",
    });
  };

  const submit = async (kind) => {
    try {
      const payload =
        kind === "approve"
          ? {
              decision: "approve",
              action: decision.action,
              quantity: decision.quantity === "" ? null : Number(decision.quantity),
              stock_item_id: decision.stock_item_id || null,
              remark: decision.remark,
              priority: decision.priority,
            }
          : { decision: kind, remark: decision.remark };
      const { data } = await api.post(`/ai/requests/${active.id}/decision`, payload);
      toast.success(
        kind === "approve"
          ? `Approved — ${data.request.erp_transaction?.type?.replace(/_/g, " ") || "recorded"}`
          : "Updated"
      );
      setActive(null);
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const runSimulation = async () => {
    if (!sim.text.trim()) return toast.error("Type the employee message");
    setProcessing(true);
    try {
      const { data } = await api.post("/ai/employee-message", {
        employee_id: sim.employee_id || undefined,
        text: sim.text,
        source: "app",
      });
      toast.success(`Understood as: ${data.intent} (${data.confidence}% confidence)`);
      setSimOpen(false);
      setSim({ employee_id: "", text: "" });
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setProcessing(false);
    }
  };

  const pending = requests.filter((r) => ["Pending Approval", "Needs Clarification", "Clarification Requested"].includes(r.status));
  const decided = requests.filter((r) => !pending.includes(r));

  return (
    <div>
      <PageHeader
        testId="ai-requests-header"
        title="AI Requests"
        subtitle="Employee reports understood by AI. Nothing touches stock or purchase until you approve."
        actions={
          can("ai", "create") && (
            <Dialog open={simOpen} onOpenChange={setSimOpen}>
              <DialogTrigger asChild>
                <Button data-testid="ai-log-message-button" className="gap-1.5">
                  <MessageSquarePlus className="h-4 w-4" /> Log employee message
                </Button>
              </DialogTrigger>
              <DialogContent data-testid="ai-message-dialog">
                <DialogHeader>
                  <DialogTitle>Employee message → AI understanding</DialogTitle>
                </DialogHeader>
                <p className="text-xs text-muted-foreground">
                  Hindi, English or Hinglish. Once the WhatsApp Business provider is configured the
                  same pipeline runs on messages received on the company number.
                </p>
                <Field label="Employee">
                  <Select value={sim.employee_id} onValueChange={(v) => setSim({ ...sim, employee_id: v })}>
                    <SelectTrigger data-testid="ai-employee-select">
                      <SelectValue placeholder="Select employee (unknown = unverified sender)" />
                    </SelectTrigger>
                    <SelectContent>
                      {employees.map((e) => (
                        <SelectItem key={e.id} value={e.id}>
                          {e.name} — {e.department || e.role_label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Message">
                  <Textarea
                    rows={3}
                    data-testid="ai-message-input"
                    placeholder="आज 20 किलो resin use हो गया"
                    value={sim.text}
                    onChange={(e) => setSim({ ...sim, text: e.target.value })}
                  />
                </Field>
                <DialogFooter>
                  <Button data-testid="ai-message-submit" disabled={processing} onClick={runSimulation}>
                    {processing && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Process with AI
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )
        }
      />

      {summary && (
        <div className="grid grid-cols-2 border-b border-border sm:grid-cols-3 lg:grid-cols-6" data-testid="ai-daily-summary">
          {[
            ["Employee reports", summary.employee_reports],
            ["Pending approvals", summary.pending_approvals],
            ["Urgent today", summary.urgent],
            ["Unverified senders", summary.unverified_senders],
            ["AI purchase requests", summary.purchase_requests_created],
            ["Top intent", summary.by_intent?.[0]?.intent || "—"],
          ].map(([k, v]) => (
            <div key={k} className="kpi border-b border-r border-border">
              <div className="overline">{k}</div>
              <div className="mt-1.5 font-mono-num text-xl font-semibold">{v}</div>
            </div>
          ))}
        </div>
      )}

      {loading ? (
        <div className="flex h-40 items-center justify-center">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <Tabs defaultValue="inbox" className="px-4 pt-4 sm:px-6">
          <TabsList data-testid="ai-tabs">
            <TabsTrigger value="inbox">Inbox ({pending.length})</TabsTrigger>
            <TabsTrigger value="history">History ({decided.length})</TabsTrigger>
            <TabsTrigger value="shortages">Recurring shortages ({shortages.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="inbox">
            {pending.length === 0 ? (
              <Empty title="Inbox is clear" hint="No employee requests waiting for a decision." />
            ) : (
              <div className="stagger grid gap-3 lg:grid-cols-2" data-testid="ai-inbox">
                {pending.map((r) => (
                  <RequestCard key={r.id} r={r} onOpen={() => openDecision(r)} canApprove={can("ai", "approve")} />
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="history">
            <div className="stagger grid gap-3 lg:grid-cols-2">
              {decided.map((r) => (
                <RequestCard key={r.id} r={r} readOnly />
              ))}
              {decided.length === 0 && <Empty title="No decided requests yet" />}
            </div>
          </TabsContent>

          <TabsContent value="shortages">
            {shortages.length === 0 ? (
              <Empty title="No recurring shortage pattern" hint="AI flags items reported short repeatedly." />
            ) : (
              <div className="grid gap-3 lg:grid-cols-2" data-testid="ai-shortages">
                {shortages.map((s) => (
                  <div key={s.item} className="surface p-4">
                    <div className="flex items-center gap-2">
                      <AlertTriangle className="h-4 w-4 text-accent" />
                      <span className="font-semibold">{s.item}</span>
                      <Badge variant="destructive" className="ml-auto text-[10px]">
                        {s.count} reports
                      </Badge>
                    </div>
                    <div className="mt-2 grid grid-cols-2 gap-2 text-xs text-muted-foreground">
                      <div>
                        Current stock:{" "}
                        <span className="font-mono-num text-foreground">
                          {s.current_stock} {s.unit}
                        </span>
                      </div>
                      <div>
                        Reorder level:{" "}
                        <span className="font-mono-num text-foreground">{s.reorder_level}</span>
                      </div>
                      <div>
                        Consumed:{" "}
                        <span className="font-mono-num text-foreground">{s.consumption_in_period}</span>
                      </div>
                      <div>
                        Avg/day:{" "}
                        <span className="font-mono-num text-foreground">{s.average_daily_usage}</span>
                      </div>
                    </div>
                    <p className="mt-2 text-xs">{s.recommendation}</p>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      )}

      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="ai-decision-dialog">
          <DialogHeader>
            <DialogTitle>Review employee request</DialogTitle>
          </DialogHeader>
          {active && (
            <>
              <div className="rounded border border-border bg-secondary/50 p-3 text-sm">
                <div className="mb-1 flex items-center gap-2">
                  <Badge variant="outline" className="text-[10px]">
                    {active.intent}
                  </Badge>
                  <Badge
                    variant={confidenceVariant(active.confidence_band)}
                    className="text-[10px]"
                  >
                    {active.confidence}% confidence
                  </Badge>
                  {!active.verified_sender && <Badge variant="destructive" className="text-[10px]">UNVERIFIED SENDER</Badge>}
                </div>
                <div className="text-xs text-muted-foreground">
                  {active.employee_name} • {active.employee_department} • {active.language}
                </div>
                <p className="mt-2">{active.transcript}</p>
                {active.audio_file_id && (
                  <audio
                    controls
                    data-testid="ai-audio-player"
                    className="mt-2 w-full"
                    src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${active.audio_file_id}`}
                  />
                )}
              </div>
              <div className="rounded border border-primary/30 bg-primary/5 p-3 text-xs">
                <div className="overline mb-1">AI inventory recommendation</div>
                <p>{active.recommendation?.note}</p>
                {active.current_stock != null && (
                  <p className="mt-1 text-muted-foreground">
                    Matched item: {active.matched_stock_name} • available {active.current_stock}{" "}
                    {active.stock_unit} • reorder level {active.reorder_level}
                  </p>
                )}
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <Field label="Action to post">
                  <Select value={decision.action} onValueChange={(v) => setDecision({ ...decision, action: v })}>
                    <SelectTrigger data-testid="ai-action-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {ACTIONS.map(([v, l]) => (
                        <SelectItem key={v} value={v}>
                          {l}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Quantity">
                  <Input
                    data-testid="ai-quantity-input"
                    value={decision.quantity}
                    onChange={(e) => setDecision({ ...decision, quantity: e.target.value })}
                  />
                </Field>
                <Field label="Stock item" className="sm:col-span-2">
                  <Select
                    value={decision.stock_item_id}
                    onValueChange={(v) => setDecision({ ...decision, stock_item_id: v })}
                  >
                    <SelectTrigger data-testid="ai-stock-select">
                      <SelectValue placeholder="Confirm material" />
                    </SelectTrigger>
                    <SelectContent>
                      {stock.map((s) => (
                        <SelectItem key={s.id} value={s.id}>
                          {s.name} ({s.quantity} {s.unit})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Remark / question to employee" className="sm:col-span-2">
                  <Textarea
                    data-testid="ai-remark-input"
                    value={decision.remark}
                    onChange={(e) => setDecision({ ...decision, remark: e.target.value })}
                  />
                </Field>
              </div>
              <DialogFooter className="flex-wrap gap-2">
                <Button variant="outline" data-testid="ai-clarify-button" onClick={() => submit("clarify")}>
                  Ask employee
                </Button>
                <Button variant="destructive" data-testid="ai-reject-button" onClick={() => submit("reject")}>
                  <XCircle className="mr-1.5 h-4 w-4" /> Reject
                </Button>
                <Button data-testid="ai-approve-button" onClick={() => submit("approve")}>
                  <CheckCircle2 className="mr-1.5 h-4 w-4" /> Approve & post
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function RequestCard({ r, onOpen, canApprove, readOnly }) {
  return (
    <div
      data-testid={`ai-request-card-${r.id}`}
      className={`surface border-l-4 p-4 ${INTENT_STYLE[r.intent] || "border-l-slate-300"}`}
    >
      <div className="flex flex-wrap items-center gap-2">
        <Badge variant="outline" className="text-[10px]">
          {r.intent}
        </Badge>
        <Badge
          variant={confidenceVariant(r.confidence_band)}
          className="text-[10px]"
        >
          {r.confidence}%
        </Badge>
        {r.entities?.urgency === "Urgent" && (
          <Badge variant="destructive" className="text-[10px]">
            URGENT
          </Badge>
        )}
        {!r.verified_sender && (
          <Badge variant="destructive" className="text-[10px]">
            UNVERIFIED
          </Badge>
        )}
        <span className="ml-auto text-[10px] text-muted-foreground">{String(r.created_at).slice(0, 16).replace("T", " ")}</span>
      </div>
      <div className="mt-2 text-sm font-medium">{r.employee_name}</div>
      <p className="mt-1 text-sm text-muted-foreground">{r.transcript}</p>
      <div className="mt-2 rounded bg-secondary/60 p-2 text-xs">
        <span className="overline">AI reading</span>
        <div className="mt-0.5">
          {r.matched_stock_name || r.entities?.material || "—"}
          {r.entities?.quantity ? ` • ${r.entities.quantity} ${r.entities.unit || ""}` : ""}
          {r.current_stock != null ? ` • in stock ${r.current_stock} ${r.stock_unit || ""}` : ""}
        </div>
        <div className="mt-1 text-muted-foreground">{r.recommendation?.note}</div>
      </div>
      <div className="mt-3 flex items-center gap-2">
        <Badge variant="secondary" className="text-[10px]">
          {r.status}
        </Badge>
        {r.erp_transaction && (
          <Badge variant="outline" className="text-[10px]">
            {r.erp_transaction.type?.replace(/_/g, " ")} {r.erp_transaction.number || ""}
          </Badge>
        )}
        {!readOnly && canApprove && (
          <Button size="sm" data-testid={`ai-review-button-${r.id}`} className="ml-auto" onClick={onOpen}>
            Review
          </Button>
        )}
      </div>
    </div>
  );
}

export function AIAssistant() {
  const [messages, setMessages] = useState([]);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api
      .get("/ai/history")
      .then(({ data }) => {
        setMessages(data.flatMap((h) => [{ role: "user", text: h.question }, { role: "ai", text: h.answer }]));
      })
      .catch((e) => logError("assistant history", e));
  }, []);

  const ask = async (question) => {
    const text = (question || q).trim();
    if (!text) return;
    setMessages((m) => [...m, { role: "user", text }]);
    setQ("");
    setBusy(true);
    try {
      const { data } = await api.post("/ai/ask", { question: text });
      setMessages((m) => [...m, { role: "ai", text: data.answer }]);
    } catch (e) {
      setMessages((m) => [...m, { role: "ai", text: apiError(e) }]);
    } finally {
      setBusy(false);
    }
  };

  const suggestions = [
    "How much did we sell this month?",
    "Which customer has the highest outstanding?",
    "What are today's urgent purchase requirements?",
    "Which quotations are pending follow-up?",
    "Which products have the highest margin?",
    "Show all expenses related to the Ganga project",
  ];

  return (
    <div className="flex h-[calc(100vh-57px)] flex-col" data-testid="ai-assistant">
      <PageHeader
        title="AI Business Assistant"
        subtitle="Answers are grounded in your live ERP data. Financial calculations stay deterministic."
      />
      <div className="flex-1 space-y-4 overflow-y-auto p-4 sm:p-6">
        {messages.length === 0 && (
          <div className="mx-auto max-w-2xl">
            <div className="mb-4 flex items-center gap-2 text-sm text-muted-foreground">
              <Bot className="h-4 w-4" /> Ask anything about sales, money, stock or employees.
            </div>
            <div className="grid gap-2 sm:grid-cols-2">
              {suggestions.map((s) => (
                <button
                  key={s}
                  data-testid={`assistant-suggestion-${s.slice(0, 10).replace(/\W+/g, "-").toLowerCase()}`}
                  onClick={() => ask(s)}
                  className="rounded-md border border-border bg-card p-3 text-left text-sm transition-colors hover:bg-secondary"
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}
        {messages.map((m, i) => (
          <div key={`${m.role}-${i}-${m.text.slice(0, 12)}`} className={`mx-auto max-w-3xl ${m.role === "user" ? "text-right" : ""}`}>
            <div
              className={`inline-block max-w-full whitespace-pre-wrap rounded-lg px-4 py-2.5 text-sm ${
                m.role === "user" ? "bg-primary text-primary-foreground" : "border border-border bg-card"
              }`}
            >
              {m.text}
            </div>
          </div>
        ))}
        {busy && (
          <div className="mx-auto max-w-3xl">
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
          </div>
        )}
      </div>
      <div className="border-t border-border p-4">
        <form
          className="mx-auto flex max-w-3xl gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            ask();
          }}
        >
          <Input
            data-testid="assistant-input"
            placeholder="Ask about your business, or tap Speak…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
          <VoiceInput purpose="assistant" label="Speak"
            onTranscript={(text) => ask(text)} />
          <Button type="submit" data-testid="assistant-send-button" disabled={busy}>
            <Send className="h-4 w-4" />
          </Button>
        </form>
      </div>
    </div>
  );
}
